<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Assignment' : 'Add Assignment'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="assignment-title" label-class="form-label">
        <b-form-input id="assignment-title" v-model="formData.title" placeholder="Enter assignment title" required />
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="assignment-description" label-class="form-label">
        <b-form-textarea
          id="assignment-description"
          v-model="formData.description"
          placeholder="Enter assignment description"
          rows="3"
          max-rows="6"
        />
      </b-form-group>

      <!-- Tasks Section -->

      <b-button variant="outline-primary" class="mr-2" @click="openTaskModal" style="justify-self: end;display: block;">
        <i class="fas fa-plus mr-1"></i> Add Task
      </b-button>

      <!-- Lessons Section -->
      <div class="form-group mb-4">
        <label class="form-label">Tasks</label>
        <draggable
          v-if="tasks && tasks.length > 0"
          v-model="tasks"
          class="mb-3"
          handle=".drag-handle"
          @end="updateTaskOrder"
        >
          <div
            v-for="(task, index) in tasks"
            :key="task.id || index"
            class="d-flex justify-content-between align-items-center"
          >
            <div class="drag-handle" style="cursor: move;">
              <i class="fas fa-bars icon-16pt mr-2"></i>
            </div>
            <div class="card mb-2" style="width: 100%">
              <div class="card-body p-3 d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0" style="font-size: medium;">{{ task.title }}</h6>
                </div>
                <div>
                  <a
                    @click="editTask(index)"
                    class="svg-icon mr-2"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.edit')"
                  >
                    <md-icon class="text-info">edit</md-icon>
                  </a>

                  <a
                    href="#"
                    @click.prevent="removeTask(index)"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.remove')"
                  >
                    <md-icon class="text-danger">delete</md-icon>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </draggable>
        <b-alert v-else show variant="info" class="mb-3">
          No tasks added yet. Click 'Add Task' to create one.
        </b-alert>
      </div>
      <b-btn variant="primary" :disabled="isSubmitting" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isSubmitting" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Create' }}</span>
      </b-btn>
    </b-form>

    <!-- Task Modal -->
    <TaskModal
      ref="taskModal"
      :modal-id="taskModalId"
      :task="currentTask"
      :is-editing="isEditingTask"
      @submit="handleTaskSubmit"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import TaskModal from './components/TaskModal.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '../../../components/Page.vue';
import draggable from 'vuedraggable';

export default {
  name: 'PostAssignments',
  components: {
    TaskModal,
    PageHeader,
    draggable,
  },
  extends: Page,

  data() {
    return {
      formData: {
        title: '',
        description: '',
      },
      tasks: [],
      currentTask: null,
      currentTaskIndex: -1,
      isEditingTask: false,
      isSubmitting: false,
      taskModalId: 'task-modal',
      isEditing: false,
    };
  },
  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Assignments', to: { name: 'list-assignments' } },
        {
          text: this.$route.params.id ? 'Edit Assignment' : 'Add Assignment',
          active: true,
        },
      ];
    },
  },

  methods: {
    ...mapActions('assignments', ['createAssignment', 'updateAssignment', 'getAssignment']),

    async fetchAssignment() {
      try {
        const response = await this.getAssignment(this.$route.params.id);
        this.formData = {
          title: response.data.title,
          description: response.data.description,
        };
        this.tasks = response.data.tasks || [];
      } catch (error) {
        console.error('Error fetching assignment:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to load assignment' });
      }
    },

    openTaskModal(task = null, index = -1) {
      this.currentTask = task
        ? { ...task }
        : {
            title: '',
            description: '',
            url: '',
            completion_mode: 'checkbox',
            order: this.tasks.length,
          };
      this.currentTaskIndex = index;
      this.isEditingTask = index >= 0;
      this.$nextTick(() => {
        this.$bvModal.show(this.taskModalId);
      });
    },

    handleTaskSubmit(taskData) {
      if (this.isEditingTask && this.currentTaskIndex >= 0) {
        // Update existing task
        this.$set(this.tasks, this.currentTaskIndex, {
          ...this.tasks[this.currentTaskIndex],
          ...taskData,
        });
      } else {
        // Add new task
        this.tasks.push({
          ...taskData,
          id: Date.now().toString(), // Temporary ID for frontend use
        });
      }
      this.$bvModal.hide(this.taskModalId);
    },
    updateTaskOrder() {
      // Update the order field of each task based on their new position
      this.tasks = this.tasks.map((task, index) => ({
        ...task,
        order: index + 1,
      }));
    },
    editTask(index) {
      if (index >= 0 && index < this.tasks.length) {
        this.openTaskModal(this.tasks[index], index);
      }
    },

    removeTask(index) {
      if (index >= 0 && index < this.tasks.length) {
        this.$bvModal
          .msgBoxConfirm('Are you sure you want to delete this task?', {
            title: 'Delete Task',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then(confirmed => {
            if (confirmed) {
              this.tasks.splice(index, 1);
            }
          });
      }
    },

    async handleSubmit() {
      if (this.tasks.length === 0) {
        this.makeToast({ variant: 'danger', msg: 'Please add at least one task' });
        return;
      }
      if (this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        const assignmentData = {
          ...this.formData,
          tasks: this.tasks,
        };

        if (this.isEditing) {
          await this.updateAssignment({
            id: this.$route.params.id,
            data: assignmentData,
          });
          this.makeToast('Assignment updated successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.$router.push({ name: 'list-assignments' });
        } else {
          await this.createAssignment(assignmentData);
          this.makeToast({ variant: 'success', msg: 'Assignment created successfully' });
          this.$router.push({ name: 'list-assignments' });
        }
      } catch (error) {
        console.error('Error saving assignment:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to save assignment' });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  async mounted() {
    if (this.$route.params.id) {
      this.isEditing = true;
      await this.fetchAssignment();
    }
  },
  filters: {
    truncate(text, length, suffix = '...') {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + suffix : text;
    },
  },
};
</script>

<style scoped>
.assignment-form {
  max-width: 800px;
  margin: 0 auto;
}

.tasks-section {
  margin-top: 2rem;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}
</style>
